<template>
  <div>
    <div class="calculator-container">
      <h1 class="h3">
        Linear Regression Calculator
      </h1>
      <div class="inputs-container">
        <div>
          <div class="label-tooltip">
            <label class="type-data">
              Label Variable X (Optional)
            </label>
            <div class="icon-info-tooltip linear-regression">
              <IconInfo />
              <Tooltip>
                <span>Please provide a name or label for the independent variable (e.g., "Square footage", "Distance to city center").</span>
              </Tooltip>
            </div>
          </div>
          <input v-model="independentName" type="text" maxLength="40">
        </div>
        <div>
          <div class="label-tooltip">
            <label class="type-data">
              Label Variable Y (Optional)
            </label>
            <div class="icon-info-tooltip linear-regression">
              <IconInfo />
              <Tooltip>
                <span>Please provide a name or label for the dependent variable (e.g., "Square footage", "Distance to city center").</span>
              </Tooltip>
            </div>
          </div>
          <input v-model="dependentName" type="text" maxLength="40">
        </div>
      </div>
      <form @submit.prevent="calculations">
        <div class="raw-data-container">
          <div class="raw-data">
            <div class="label-tooltip">
              <label class="type-data">
                Independent Variable ({{ independentNameChecked }}) Values
              </label>
              <div class="icon-info-tooltip linear-regression">
                <IconInfo />
                <Tooltip>
                  <span>Input the data separated by comma, space or enter.</span>
                </Tooltip>
              </div>
            </div>
            <div class="textarea-border" :class="{ 'invalid-value': textareaAlertX }">
              <textarea
                v-model="rawDataX"
                cols="30" rows="10"
                placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
              />
            </div>
            <div v-if="textareaAlertX" class="error">
              {{ textareaAlertX }}
            </div>
          </div>
          <div class="raw-data">
            <div class="label-tooltip">
              <label class="type-data">
                Dependent Variable ({{ dependentNameChecked }}) Values
              </label>
              <div class="icon-info-tooltip linear-regression">
                <IconInfo />
                <Tooltip>
                  <span>Input the data separated by comma, space or enter.</span>
                </Tooltip>
              </div>
            </div>
            <div class="textarea-border" :class="{ 'invalid-value': textareaAlertY }">
              <textarea
                v-model="rawDataY"
                cols="30" rows="10"
                placeholder="7,1,6,2,11

7 1 6 2 11

7
1
6"
              />
            </div>
            <div v-if="textareaAlertY" class="error">
              {{ textareaAlertY }}
            </div>
          </div>
        </div>
        <div class="decimal-places">
          <div class="label-tooltip">
            <label>
              Decimal Places
            </label>
            <div class="icon-info-tooltip coefficient-of-variation">
              <IconInfo />
              <Tooltip>
                <span>Choose the number of decimal places.</span>
              </Tooltip>
            </div>
          </div>
          <Dropdown
            calculator
            calculator-decimal-places
            class="small-dropdown"
            :selected="selectedDecimalPlaces"
            :radio-buttons="{ 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7', 8: '8', 9: '9' }"
            @change="currentSelectedDecimalPlace"
          />
        </div>
        <div class="buttons">
          <button class="btn-primary calculate">
            Calculate
          </button><button
            class="btn-primary-light reset"
            @click="reset"
          >
            Reset
          </button>
          <button class="load-example btn-link-accent" @click="loadExample">
            Load Example
          </button>
        </div>
      </form>
    </div>

    <div
      v-show="showSummaryInterpretation" id="summary" class="summary-container"
    >
      <h4>Summary:</h4>
      <div class="results">
        <div>
          <p class="result-name">
            Regression Equation:
          </p>
          <p class="result-value">
            <span>\[\hat{y}\]</span> = {{ variablesToShowArr[0] }} + {{ variablesToShowArr[1] }}<span>\[x_1\]</span>
          </p>
        </div>
        <div>
          <p class="result-name">
            Correlation:
          </p>
          <p class="result-value">
            {{ variablesToShowArr[2] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            R Square:
          </p>
          <p class="result-value">
            {{ variablesToShowArr[3] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Standard Error:
          </p>
          <p class="result-value">
            {{ variablesToShowArr[4] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            F-Value:
          </p>
          <p class="result-value">
            {{ variablesToShowArr[5] }}
          </p>
        </div>
        <div>
          <p class="result-name">
            Probability (F-Value):
          </p>
          <p class="result-value">
            {{ variablesToShowArr[6] }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="showSummaryInterpretation" class="interpretation-container">
      <h4>Interpretation:</h4>
      <p v-if="correlation !== 0">
        The correlation coefficient (R) equals <span class="colored-texts">{{ variablesToShowArr[2] }}</span>. This means that there is a <span class="colored-texts">{{ interpretationCorrelation }}</span> between independent variable (<span>{{ independentNameChecked }}</span>) and dependent variable (<span>{{ dependentNameChecked }}</span>).
      </p>
      <p v-else>
        The correlation coefficient (R) equals 0. This means that there is no linear relationship between the independent variable (<span>{{ independentNameChecked }}</span>) and dependent variable (<span>{{ dependentNameChecked }}</span>).
      </p>
      <p>The coefficient of determination (R2) equals <span class="colored-texts">{{ variablesToShowArr[3] }}</span>. This means that <span class="colored-texts">{{ rSquaredPercentage }}</span>% of the variation in the dependent variable (<span>{{ dependentNameChecked }}</span>) is explained by the independent variable (<span>{{ independentNameChecked }}</span>) included in the regression model.</p>
      <p>The slope of the regression line <span class="inline-formulas">\[b_1\]</span> equals <span class="colored-texts">{{ variablesToShowArr[1] }}</span>. This means that for every one unit increase in the independent variable (<span>{{ independentNameChecked }}</span>), the dependent variable (<span>{{ dependentNameChecked }}</span>) is expected to increase by <span class="colored-texts">{{ variablesToShowArr[1] }}</span> units.</p>
      <p>The F-test (1, <span class="colored-texts">{{ degreesOfFreedom }}</span>) equals <span class="colored-texts">{{ variablesToShowArr[5] }}</span> and its p-value is equal to <span class="colored-texts">{{ variablesToShowArr[6] }}</span>. This indicates that the regression model is <span class="colored-texts">{{ significantOrNot }}</span> at <span class="colored-texts">{{ levelOfSignificancePercentage }}% level of significance</span>.</p>
    </div>

    <div v-if="showSummaryInterpretation" class="chart-wrapper">
      <div class="download-section">
        <div class="header-legend">
          <h4 class="header">
            Estimated Regression Line
          </h4>
          <div class="legend">
            <div class="regression-line">
              <span class="line" />
              <p>Regression Line ({{ dependentNameCheckedGraph }}=2{{ independentNameCheckedGraph }}+3)</p>
            </div>
            <div class="independent-dependant">
              <span class="circle dependant" />
              <p>{{ independentNameCheckedGraph }}</p>
            </div>
            <div class="independent-dependant">
              <span class="circle" />
              <p>Estimated {{ dependentNameCheckedGraph }}</p>
            </div>
          </div>
        </div>
        <div class="chart">
          <Chart
            id="linear-regression"
            type="line" :data="chart"
            :options="defaultChartOptions"
          />
        </div>
      </div>
      <button class="btn-link-accent" @click="downloadImage">
        Download
      </button>
    </div>

    <div v-if="showSummaryInterpretation" class="two-tables">
      <h4>ANOVA Table</h4>
      <div class="gradient-wrapper gradient-first" :class="{ 'no-gradient-after': endScrolledAfterFirstTable, 'no-gradient-before': endScrolledBeforeFirstTable }">
        <div class="first-table-wrapper" @scroll="handleScroll('FirstTable', $event)">
          <table>
            <thead>
              <tr>
                <th class="headings">
                  <div class="cells th" />
                </th>
                <th>df</th>
                <th>Sum of Squares(SS)</th>
                <th>Mean Sum of Squares (MS)</th>
                <th>F-Value</th>
                <th>Probability (F-Value)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="headings">
                  Regression
                </td>
                <td>1</td>
                <td>{{ sumOfSquaresMean[0] }}</td>
                <td>{{ sumOfSquaresMean[0] }}</td>
                <td>{{ variablesToShowArr[5] }}</td>
                <td>{{ variablesToShowArr[6] }}</td>
              </tr>
              <tr>
                <td class="headings">
                  Residual
                </td>
                <td>{{ degreesOfFreedom }}</td>
                <td>{{ sumOfSquaresMean[1] }}</td>
                <td>{{ sumOfSquaresMean[2] }}</td>
                <td />
                <td />
              </tr>
              <tr>
                <td class="headings">
                  <div class="cells td">
                    <p>Total</p>
                  </div>
                </td>
                <td>{{ totalDegreesOfFreedom }}</td>
                <td>{{ sumOfSquaresMean[3] }}</td>
                <td />
                <td />
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <h4>Coefficients Table</h4>
      <div>
        <div class="gradient-wrapper gradient-second" :class="{ 'no-gradient-after': endScrolledAfterSecondTable, 'no-gradient-before': endScrolledBeforeSecondTable }">
          <div class="second-table-wrapper" @scroll="handleScroll('SecondTable', $event)">
            <table>
              <thead>
                <tr>
                  <th class="headings">
                    <div class="cells th" />
                  </th>
                  <th>Coefficients</th>
                  <th>Standard Error</th>
                  <th>t Stat</th>
                  <th>P-value</th>
                  <th>Lower 95%</th>
                  <th>Upper 95%</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="headings">
                    Intercept
                  </td>
                  <td>{{ variablesToShowArr[0] }}</td>
                  <td>{{ variablesToShowArr[8] }}</td>
                  <td>{{ variablesToShowArr[10] }}</td>
                  <td>{{ variablesToShowArr[7] }}</td>
                  <td>{{ variablesToShowArr[12] }}</td>
                  <td>{{ variablesToShowArr[13] }}</td>
                </tr>
                <tr>
                  <td class="headings">
                    <div class="cells td">
                      <p>{{ independentNameChecked }} Variable</p>
                    </div>
                  </td>
                  <td>{{ variablesToShowArr[1] }}</td>
                  <td>{{ variablesToShowArr[9] }}</td>
                  <td>{{ variablesToShowArr[11] }}</td>
                  <td>{{ variablesToShowArr[6] }}</td>
                  <td>{{ variablesToShowArr[14] }}</td>
                  <td>{{ variablesToShowArr[15] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import calculatorsMainMixin from '@itbpbg/lms-components/src/mixins/calculators/main';
import Dropdown from '@itbpbg/lms-components/src/components/Dropdown.vue';
import Tooltip from '@itbpbg/lms-components/src/components/Tooltip.vue';
import IconInfo from '@itbpbg/lms-components/src/assets/svg/icon-info.svg?vue-component';
import jStat from 'jstat';
import html2canvas from 'html2canvas';
import Chart from '../../../Chart';

export default {
  components: {
    Dropdown,
    Tooltip,
    IconInfo,
    Chart,
  },
  mixins: [calculatorsMainMixin],
  data() {
    return {
      rawDataX: '',
      rawDataY: '',
      inputArrays: [],
      textareaAlertX: false,
      textareaAlertY: false,
      resultNumber: 0,
      selectedDecimalPlaces: '4',
      showSummaryInterpretation: false,
      slopeOfRegressionLine: 0,
      correlation: 0,
      rSquared: 0,
      rSquaredPercentage: 0,
      standardError: 0,
      regressionLineIntercept: 0,
      interpretationCorrelation: '',
      fTest: 0,
      degreesOfFreedom: 0,
      levelOfSignificancePercentage: 0,
      significantOrNot: '',
      independentName: 'X',
      dependentName: 'Y',
      independentNameChecked: 'X',
      dependentNameChecked: 'Y',
      independentNameCheckedGraph: 'X',
      dependentNameCheckedGraph: 'Y',
      variablesToShowArr: [],
      totalDegreesOfFreedom: 0,
      sumOfSquaresMean: [],
      endScrolledAfterFirstTable: true,
      endScrolledBeforeFirstTable: true,
      endScrolledAfterSecondTable: true,
      endScrolledBeforeSecondTable: true,
      graphStylesArr: {
        axesSpaces: [20, 5],
        axesFontSize: [20, 12],
        ticksSize: [14, 10],
      },
      graphVariableNamesFormatting: {
        limit: 25,
        substringEnd: 19,
      },
    };
  },
  computed: {
    graphLayoutIndex() {
      return this.$store.state.layout === 'desktop' ? 0 : 1;
    },
    chart() {
      let data = this.inputArrays[0].map((x, i) => ({ x, y: this.inputArrays[1][i] }));
      let regression = [];
      const residualArr = [];

      for (let i = 0; i < this.inputArrays[0].length; i += 1) {
        const yVal = this.regressionLineIntercept + this.slopeOfRegressionLine * this.inputArrays[0][i];
        regression.push({ x: this.inputArrays[0][i], y: yVal.toFixed(this.selectedDecimalPlaces) });
        residualArr.push(this.inputArrays[1][i] - yVal);
      }

      data = Array.from(new Set(data.map(JSON.stringify))).map(JSON.parse);
      regression = Array.from(new Set(regression.map(JSON.stringify))).map(JSON.parse);
      data.push({ dependantName: this.dependentNameCheckedGraph });
      regression.push({ dependantName: this.dependentNameCheckedGraph });

      this.residualCalculations(residualArr);

      return {
        datasets: [
          {
            label: 'Scattered',
            data,
            backgroundColor: '#56BFC7',
            type: 'scatter',
          },
          {
            label: 'Regression Line (y=2X+3)',
            data: regression,
            borderColor: '#29757A',
            backgroundColor: '#EDC843',
            type: 'line',
          },
        ],
      };
    },
    defaultChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            ticks: {
              font: {
                family: 'Mazzard',
                weight: 400,
                size: this.graphStylesArr.ticksSize[this.graphLayoutIndex],
              },
              color: '#000C1F',
            },
            title: {
              display: true,
              text: this.dependentNameCheckedGraph,
              padding: {
                top: 0,
                bottom: this.$store.state.layout === 'desktop' ? 10 : 0,
              },
              font: {
                family: 'Mazzard',
                weight: 500,
                size: this.graphStylesArr.axesFontSize[this.graphLayoutIndex],
              },
              color: '#748190',
            },
          },
          x: {
            type: 'linear',
            ticks: {
              font: {
                family: 'Mazzard',
                weight: 400,
                size: this.graphStylesArr.ticksSize[this.graphLayoutIndex],
              },
              color: '#000C1F',
            },
            title: {
              display: true,
              text: this.independentNameCheckedGraph,
              padding: {
                top: this.graphStylesArr.axesSpaces[this.graphLayoutIndex],
                bottom: 0,
              },
              font: {
                family: 'Mazzard',
                weight: 500,
                size: this.graphStylesArr.axesFontSize[this.graphLayoutIndex],
              },
              color: '#748190',
            },
          },
        },
        plugins: {
          tooltip: {
            displayColors: false,
            animation: false,
            callbacks: {
              title(ctx) {
                if (ctx[0].dataset.label === 'Scattered') {
                  return ctx[0].dataset.data[ctx[0].dataset.data.length - 1].dependantName;
                }

                return `Estimated ${ctx[0].dataset.data[ctx[0].dataset.data.length - 1].dependantName}`;
              },
              label(ctx) {
                return `${ctx.parsed.x}, ${ctx.parsed.y}`;
              },
            },
          },
          legend: {
            display: false,
          },
        },
        chartArea: {
          backgroundColor: '#FFFFFF',
        },
        linearRegressionBorder: {
          borderColor: '#29757A',
          borderWidth: 1.30,
        },
      };
    },
    rawDataArray() {
      return [
        this.rawDataX,
        this.rawDataY,
        this.independentName,
        this.dependentName,
        this.selectedDecimalPlaces,
      ];
    },
    independentDependentArr() {
      return [
        this.independentName,
        this.dependantName,
      ];
    },
  },
  watch: {
    rawDataArray() {
      this.hideAlertsResults('inputs');
    },
    independentDependentArr() {
      if (this.$store.state.laptop) {
        this.graphVariableNamesFormatting.limit = 15;
        this.graphVariableNamesFormatting.substringEnd = 13;
      } else if (this.$store.state.layout === 'mobile') {
        this.graphVariableNamesFormatting.limit = 13;
        this.graphVariableNamesFormatting.substringEnd = 10;
      }
    },
    independentName() {
      this.independentNameChecked = this.independentName.length > 6
        ? `${this.independentName.substring(0, 6)}...`
        : this.independentName;

      this.independentNameCheckedGraph = this.independentName.length > this.graphVariableNamesFormatting.limit
        ? `${this.independentName.substring(0, this.graphVariableNamesFormatting.substringEnd)}...`
        : this.independentName;
    },
    dependentName() {
      this.dependentNameChecked = this.dependentName.length > 7
        ? `${this.dependentName.substring(0, 6)}...`
        : this.dependentName;

      this.dependentNameCheckedGraph = this.dependentName.length > this.graphVariableNamesFormatting.limit
        ? `${this.dependentName.substring(0, this.graphVariableNamesFormatting.substringEnd)}...`
        : this.dependentName;
    },
  },
  methods: {
    handleScroll(currentTable, event) {
      const { scrollLeft, scrollWidth, offsetWidth } = event.target;
      const contentWidth = scrollWidth - offsetWidth;

      if (contentWidth <= scrollLeft + 10) {
        this[`endScrolledAfter${currentTable}`] = true;
      } else {
        this[`endScrolledAfter${currentTable}`] = false;
      }

      if (scrollLeft - 10 >= 0) {
        this[`endScrolledBefore${currentTable}`] = false;
      } else {
        this[`endScrolledBefore${currentTable}`] = true;
      }
    },
    isOverflown() {
      const firstTable = document.getElementsByClassName('first-table-wrapper')[0];
      const secondTable = document.getElementsByClassName('second-table-wrapper')[0];

      if (firstTable.scrollWidth > firstTable.clientWidth) {
        this.endScrolledAfterFirstTable = false;
      }

      if (secondTable.scrollWidth > secondTable.clientWidth) {
        this.endScrolledAfterSecondTable = false;
      }
    },
    async downloadImage() {
      const watermarkImg = new Image();
      watermarkImg.src = require('../../../../assets/images/calculators/linear-regression/watermark.png');

      await html2canvas(document.querySelector('.download-section')).then((canvas) => {
        const img = new Image();
        img.src = canvas.toDataURL();

        img.onload = () => {
          const watermarkWidthPosition = img.width - (this.$store.state.layout === 'mobile' ? 150 : 140);
          const watermarkHeightPosition = img.height - (this.$store.state.layout === 'mobile' ? 40 : 30);
          const currentCanvas = document.createElement('canvas');

          currentCanvas.width = img.width;
          currentCanvas.height = img.height;

          const ctx = currentCanvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          ctx.drawImage(watermarkImg, watermarkWidthPosition, watermarkHeightPosition);

          const link = document.createElement('a');
          link.download = 'linear-regression-graph.jpeg';
          link.href = currentCanvas.toDataURL();
          link.click();
        };
      });
    },
    hideAlertsResults(inputs) {
      this.$store.commit('calculators/result', false);
      this.textareaAlertX = false;
      this.textareaAlertY = false;
      this.showSummaryInterpretation = false;
      this.variablesToShowArr = [];
      this.sumOfSquaresMean = [];

      if (!inputs) {
        this.dependentName = 'Y';
        this.independentName = 'X';
        this.selectedDecimalPlaces = '4';
      }
    },
    reset(e) {
      this.rawDataX = '';
      this.rawDataY = '';
      this.hideAlertsResults();

      e.preventDefault();
    },
    loadExample(e) {
      this.rawDataX = '80\n75\n86\n75\n61\n68\n72\n66\n61\n79';
      this.rawDataY = '90\n85\n107\n71\n65\n69\n67\n70\n65\n81';
      this.hideAlertsResults();

      e.preventDefault();
    },
    currentInputCheck(arr, currentTextarea) {
      if (arr.length === 0) {
        this[`textareaAlert${currentTextarea}`] = 'Field required';

        return false;
      }

      for (let i = 0; i < arr.length; i += 1) {
        if (arr[i] < 0 || Number.isNaN(Number(arr[i]))) {
          this[`textareaAlert${currentTextarea}`] = 'Invalid value';

          return false;
        }
      }

      if (arr.length < 3 && arr.length > 0) {
        this[`textareaAlert${currentTextarea}`] = 'Make sure that the X and Y columns have a minimum of three values each.';

        return false;
      }

      if (this.inputArrays[0].length !== this.inputArrays[1].length) {
        this[`textareaAlert${currentTextarea}`] = 'Ensure that the number of values entered in the X and Y columns are the same.';

        return false;
      }

      if (arr.every((el) => el === arr[0])) {
        this[`textareaAlert${currentTextarea}`] = `Invalid data: All ${currentTextarea} values are identical`;

        return false;
      }

      return true;
    },
    inputValidation() {
      let firstReturn;

      if (!firstReturn) {
        this.textareaAlertX = false;
        this.textareaAlertY = false;
      }

      this.inputArrays = [this.rawDataX, this.rawDataY].map((arr) => arr.split(/,|\s+|\n+/).filter((value) => value !== ''));

      firstReturn = this.currentInputCheck(this.inputArrays[0], 'X');
      const secondReturn = this.currentInputCheck(this.inputArrays[1], 'Y');

      if (!firstReturn || !secondReturn) {
        return false;
      }

      return true;
    },
    calculations() {
      if (!this.inputValidation()) {
        return;
      }

      this.endScrolledAfterFirstTable = true;
      this.endScrolledAfterSecondTable = true;
      this.endScrolledBeforeFirstTable = true;
      this.endScrolledBeforeSecondTable = true;

      // Turn all values into numbers
      this.inputArrays = this.inputArrays.map((arr) => arr.map(Number));

      // Calculate the mean for X and Y
      const meansArray = this.inputArrays.map((arr) => arr.reduce((acc, num) => acc + num, 0) / arr.length);

      // Creating two arrays from the results of the mean subtracted from the values for X and Y
      const valuesMinusMeanArrays = this.inputArrays.map((arr, i) => arr.map((num) => num - meansArray[i]));

      // Calculate the sum of products of the deviations of X and Y
      let sumOfProducts = [];
      for (let i = 0; i < valuesMinusMeanArrays[0].length; i += 1) {
        sumOfProducts.push(valuesMinusMeanArrays[0][i] * valuesMinusMeanArrays[1][i]);
      }
      sumOfProducts = sumOfProducts.reduce((acc, num) => acc + num);

      // Calculate squared deviations of X
      const squaredDeviationsOfX = valuesMinusMeanArrays[0].map((num) => num ** 2).reduce((acc, num) => acc + num);

      // Calculating the slope of the regression line
      this.slopeOfRegressionLine = sumOfProducts / squaredDeviationsOfX;

      // Calculating the intercept of the regression line
      this.regressionLineIntercept = meansArray[1] - (this.slopeOfRegressionLine * meansArray[0]);

      // -----------------------------------------------------------------------------------------

      this.additionalCalculations(meansArray, this.regressionLineIntercept);
      this.showSummaryInterpretation = true;
      this.calculationData(meansArray, sumOfProducts, squaredDeviationsOfX);
      this.$store.commit('calculators/result', this.resultNumber += 1);
      this.scrollToSection();
    },
    additionalCalculations(meansArray, intercept) {
      // Correlation ----------------------------------------------------------------------------

      const n = this.inputArrays[0].length;
      const sumX = this.inputArrays[0].reduce((acc, num) => acc + num);
      const sumY = this.inputArrays[1].reduce((acc, num) => acc + num);
      const sumXY = this.inputArrays[0].map((xi, i) => xi * this.inputArrays[1][i]).reduce((acc, num) => acc + num);
      const sumX2 = this.inputArrays[0].map((xi) => xi * xi).reduce((acc, num) => acc + num);
      const sumY2 = this.inputArrays[1].map((yi) => yi * yi).reduce((acc, num) => acc + num);

      // Calculate correlation
      this.correlation = (n * sumXY - sumX * sumY) / Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

      // R-squared ------------------------------------------------------------------------------

      // Calculate SSR
      const yPred = this.inputArrays[0].map((xi) => this.slopeOfRegressionLine * xi + intercept);
      const ssr = yPred.map((yiPred) => (yiPred - meansArray[1]) ** 2).reduce((acc, num) => acc + num);
      this.sumOfSquaresMean.push(ssr);

      // Calculate SST
      const sst = this.inputArrays[1].reduce((acc, yi) => acc + (yi - meansArray[1]) ** 2, 0);

      // Calculate R-squared
      this.rSquared = ssr / sst;
      this.rSquaredPercentage = (this.rSquared * 100).toFixed(this.selectedDecimalPlaces);

      // Standard Error --------------------------------------------------------------------------

      // Calculate SSE
      const sse = yPred.reduce((acc, cur, i) => acc + (cur - this.inputArrays[1][i]) ** 2, 0);

      // Calculate SER
      this.standardError = Math.sqrt(sse / (n - 2));

      // Interpretation --------------------------------------------------------------------------

      if (this.correlation > 0 && this.correlation < 0.3) {
        this.interpretationCorrelation = 'weak positive correlation';
      } else if (this.correlation < 0.5) {
        this.interpretationCorrelation = 'moderate positive correlation';
      } else if (this.correlation < 1) {
        this.interpretationCorrelation = 'strong positive correlation';
      } else if (this.correlation > -0.3) {
        this.interpretationCorrelation = 'weak negative correlation';
      } else if (this.correlation > -0.5) {
        this.interpretationCorrelation = 'moderate negative correlation';
      } else if (this.correlation > -1) {
        this.interpretationCorrelation = 'strong moderate correlation';
      } else if (this.correlation === 1) {
        this.interpretationCorrelation = 'perfect positive linear relationship';
      } else if (this.correlation === -1) {
        this.interpretationCorrelation = 'perfect negative linear relationship';
      }

      // Calculate the F-test value
      const MST = ssr / 1;
      const MSE = sse / (this.inputArrays[1].length - 2);
      this.fTest = MST / MSE;

      // Calculating standard error for intercept and x-variable
      const xVariableSer = this.inputArrays[0].reduce((acc, num) => acc + ((num - meansArray[0]) ** 2), 0);
      const interceptSer = (1 / n) + ((meansArray[0] ** 2) / xVariableSer);
      const interceptXVariableSerArr = [Math.sqrt(MSE) * Math.sqrt(interceptSer), Math.sqrt(MSE) / Math.sqrt(xVariableSer)];

      // Calculating the p-values
      this.degreesOfFreedom = n - 2;
      this.totalDegreesOfFreedom = this.degreesOfFreedom + 1;
      const pValuesArr = [
        1 - jStat.centralF.cdf(this.fTest, 1, this.degreesOfFreedom),
        2 * (jStat.studentt.cdf(-Math.abs(this.regressionLineIntercept / interceptXVariableSerArr[0]), this.degreesOfFreedom)),
      ];

      // Level of significance
      this.levelOfSignificancePercentage = pValuesArr[0] < 0.01 ? 1 : 5;
      this.significantOrNot = pValuesArr[0] < 0.01 ? 'statistically significant' : 'not statistically significant';

      // Calculate upper and lower bound
      const tScore = Math.abs(jStat.studentt.inv(0.025, this.degreesOfFreedom));
      const tStat = [this.regressionLineIntercept / interceptXVariableSerArr[0], this.slopeOfRegressionLine / interceptXVariableSerArr[1]];
      const upperLowerBound = [
        this.regressionLineIntercept - interceptXVariableSerArr[0] * tScore,
        this.regressionLineIntercept + interceptXVariableSerArr[0] * tScore,
        this.slopeOfRegressionLine - interceptXVariableSerArr[1] * tScore,
        this.slopeOfRegressionLine + interceptXVariableSerArr[1] * tScore,
      ];

      // ----------------------------------------------------------------------------------------

      const results = [
        this.regressionLineIntercept,
        this.slopeOfRegressionLine,
        this.correlation,
        this.rSquared,
        this.standardError,
        this.fTest,
        pValuesArr[0],
        pValuesArr[1],
        interceptXVariableSerArr[0],
        interceptXVariableSerArr[1],
        tStat[0],
        tStat[1],
        upperLowerBound[0],
        upperLowerBound[1],
        upperLowerBound[2],
        upperLowerBound[3],
      ];

      this.checkAndFormatResult(results);
    },
    residualCalculations(residualArr) {
      this.sumOfSquaresMean.push(residualArr.reduce((acc, num) => acc + (num ** 2), 0));
      this.sumOfSquaresMean.push(this.sumOfSquaresMean[1] / this.degreesOfFreedom);
      this.sumOfSquaresMean.push(this.sumOfSquaresMean[0] + this.sumOfSquaresMean[1]);

      this.checkAndFormatResult(this.sumOfSquaresMean);
    },
    checkAndFormatResult(arr) {
      const results = arr.slice();

      if (results.length === 4) {
        this.sumOfSquaresMean = [];
      }

      const currentArray = results.length === 4 ? 'sumOfSquaresMean' : 'variablesToShowArr';
      const onlyZerosTest = /^\d+.[0]+$/;

      for (let i = 0; i < results.length; i += 1) {
        if (results[i].toString().split('').includes('e')) {
          let numberPart = 0;
          let additionalPart = '';

          [numberPart, additionalPart] = results[i].toString().split(/(?=e)/);
          numberPart = Number(numberPart);

          if (onlyZerosTest.test(numberPart)) {
            numberPart = Math.trunc(numberPart);
          } else {
            numberPart = numberPart.toFixed(this.selectedDecimalPlaces);
          }

          results[i] = numberPart.toString().concat(additionalPart);
        }

        if (results[i] % 1 === 0 || typeof results[i] === 'string') {
          this[currentArray].push(results[i]);
        } else if (onlyZerosTest.test(results[i])) {
          results[i] = Math.trunc(results[i]);
          this[currentArray].push(results[i]);
        } else {
          this[currentArray].push(results[i].toFixed(this.selectedDecimalPlaces));
        }
      }

      this.$nextTick(() => { this.isOverflown(); });
    },
    calculationData(meansArray, sumOfProducts, squaredDeviationsOfX) {
      this.$store.commit('calculators/calculationData',
        {
          inputArrays: this.inputArrays,
          meansArray,
          sumOfProducts,
          selectedDecimalPlaces: this.selectedDecimalPlaces,
          squaredDeviationsOfX,
          slope: this.slopeOfRegressionLine,
          intercept: this.regressionLineIntercept,
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.calculator-container {
  .label-tooltip .icon-info-tooltip {
    margin: 0;
  }

  .inputs-container {
    display: flex;
    margin-bottom: 17px;
    gap: var(--spacing-30);
  }

  .raw-data-container {
    display: flex;
    gap: var(--spacing-30);

    .raw-data {
      margin-top: 0;
    }
  }

  .label-tooltip.your-data-tooltip {
    .your-data {
      margin-bottom: 0;
    }

    .icon-info-tooltip {
      &.linearRegression {
        &:hover {
          .tooltip {
            top: 28px;
            bottom: unset;

            &:after {
              top: -11px;
              rotate: 180deg;
            }
          }
        }
      }
    }
  }
}

.summary-container {
  min-height: unset !important;

  .results {
    min-height: unset !important;

    div {
      justify-content: space-between;
      padding: 0 var(--spacing-80) 0 10px;

      &:nth-child(even) {
        background: rgba(218, 246, 251, 0.55);
      }

      .result-name {
        width: unset;
      }

      .result-value {
        text-wrap: nowrap;

        span {
          ::v-deep .MathJax_SVG_Display {
            display: unset !important;
          }
        }
      }
    }
  }
}

.chart-wrapper {
  display: flex;
  flex-direction: column;
  margin-top: var(--spacing-30);
  border: 1px solid rgba(0, 12, 31, 0.18);
  border-radius: 10px;
  background: var(--accent-light-1);

  .download-section {
    padding: var(--spacing-30) var(--spacing-30);

    .header-legend {
      display: flex;
      flex-direction: column;
      align-items: center;

      .header {
        margin-bottom: var(--spacing-30);
      }

      .legend {
        display: flex;
        flex-direction: column;
        border: 1px solid var(--main-dark);
        border-radius: 5px;
        padding: 15px;
        gap: 15px;
        margin-bottom: var(--spacing-30);

        p {
          margin: 0;
          font-size: 14px;
          font-weight: 500;
        }

        .line,
        .circle {
          margin-right: var(--spacing-6);
        }

        .regression-line {
          display: flex;
          align-items: center;

          .line {
            width: 40px;
            height: 10px;
            border-radius: 3px;
            background: var(--accent-shade-3);
          }
        }

        .independent-dependant {
          display: flex;
          align-items: center;

          .circle {
            width: 15px;
            height: 15px;
            background: var(--main-yellow);
            border-radius: 50%;

            &.dependant {
              background: var(--accent);
            }
          }
        }
      }
    }
  }

  button {
    margin: 0 var(--spacing-30) var(--spacing-60) auto;
    font-family: Mazzard, sans-serif;
  }
}

.two-tables {
  margin-top: var(--spacing-48);

  .gradient-wrapper {
    &.gradient-first {
      position: relative;

      &:after {
        content: "";
        width: 73px;
        height: calc(100% - 36px);
        position: absolute;
        right: 0;
        top: 0;
        background: linear-gradient(270deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
      }

      &:before {
        content: "";
        width: 73px;
        height: calc(100% - 36px);
        position: absolute;
        top: 0;
        left: 100px;
        background: linear-gradient(90deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
      }
    }

    &.gradient-second {
      position: relative;

      &:after {
        content: "";
        width: 73px;
        height: calc(100% - 36px);
        position: absolute;
        right: 0;
        top: 0;
        background: linear-gradient(270deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
      }

      &:before {
        content: "";
        width: 73px;
        height: calc(100% - 36px);
        position: absolute;
        top: 0;
        left: 100px;
        background: linear-gradient(90deg, var(--main-light) 1.64%, rgba(255, 255, 255, 0) 84.62%);
      }
    }

    &.no-gradient-after {
      &:after {
        content: none;
      }
    }

    &.no-gradient-before {
      &:before {
        content: none;
      }
    }

    .first-table-wrapper,
    .second-table-wrapper {
      overflow: auto;
      padding-bottom: var(--spacing-30);

      &::-webkit-scrollbar {
        height: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: var(--main-light-shade-3);
        border-radius: 10px;
      }

      table {
        width: 100%;
        border-spacing: 0;

        thead,
        tbody {
          tr:first-child th:last-child { border-top-right-radius: 5px; }
          tr:last-child td:last-child { border-bottom-right-radius: 5px; }

          tr {
            th,
            td {
              padding: 0 5px;
              text-align: center;
              min-width: 100px;
              height: 56px;
              border: 1px solid var(--accent-shade-3);
              background: var(--main-light);

              &:nth-child(2) {
                border-left: unset;
              }
            }

            &:last-of-type {
              th {
                &:not(:first-child) {
                  border-top: 2px solid var(--accent-shade-3);
                }

                &:last-child {
                  border-right: 2px solid var(--accent-shade-3);
                }
              }

              td {
                &:not(:first-child) {
                  border-bottom: 2px solid var(--accent-shade-3);
                }

                &:last-child {
                  border-right: 2px solid var(--accent-shade-3);
                }
              }

              th:first-of-type,
              td:first-of-type {
                padding: 0;
                background: var(--main-light-shade-1);
              }

              th:first-of-type {
                border-top: unset;
                border-left: unset;
              }

              td:first-of-type {
                border-left: unset;
                border-bottom: unset;
              }
            }

            &:not(:last-of-type) {
              td:last-child {
                border-right: 2px solid var(--accent-shade-3);
              }
            }

            .headings {
              position: sticky;
              border-left: 2px solid var(--accent-shade-3);
              border-right: 2px solid var(--accent-shade-3);
              font-weight: 500;
              left: 0;

              .cells {
                padding: 0 5px;
                min-width: 100px;
                height: 56px;

                &.th {
                  border-top: 2px solid var(--accent-shade-3);
                  border-left: 2px solid var(--accent-shade-3);
                  background: var(--accent-shade-1);
                  border-top-left-radius: 5px;
                }

                &.td {
                  display: flex;
                  border-bottom: 2px solid var(--accent-shade-3);
                  border-left: 2px solid var(--accent-shade-3);
                  border-bottom-left-radius: 5px;
                  justify-content: center;
                  align-items: center;
                }

                p {
                  margin: 0;
                  font-weight: 500;
                }
              }
            }
          }
        }

        thead tr {
          th {
            background: var(--accent-shade-1);
            color: var(--main-light);
            font-weight: 600;
          }
        }
      }
    }

    .first-table-wrapper {
      margin-bottom: var(--spacing-18);

      table {
        thead tr {
          th:nth-child(1) {
            width: 100px;
          }
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .two-tables {
    .gradient-wrapper {
      .first-table-wrapper,
      .second-table-wrapper {
        thead tr {
          th {
            font-size: 14px;
          }
        }

        tbody tr {
          th {
            font-size: 14px;
          }

          .headings {
            font-size: 14px;
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .calculator-container {
    .decimal-places {
      width: 100% !important;

      .label-tooltip .icon-info-tooltip {
        &.linear-regression {
          &:hover {
            .tooltip:after {
              left: 49.7%;
            }
          }
        }
      }
    }

    .inputs-container {
      flex-direction: column;
    }

    .raw-data-container {
      flex-direction: column;

      .raw-data {
        .textarea-border textarea {
          height: 100px;
        }

        .label-tooltip .icon-info-tooltip {
          &.linear-regression {
            &:hover {
              .tooltip {
                left: -160%;

                &:after {
                  left: 57.7%;
                }
              }
            }
          }
        }
      }
    }
  }

  .label-tooltip.your-data-tooltip .icon-info-tooltip {
    &.linear-regression {
      &:hover {
        .tooltip {
          left: 18px;

          &:after {
            left: 43.11%;
          }
        }
      }
    }
  }

  div .summary-container {
    margin-top: var(--spacing-60);
  }

  .chart-wrapper {
    padding: var(--spacing-32) var(--spacing-24) var(--spacing-56);
    border: none;
    margin-top: var(--spacing-48);

    .download-section {
      padding: 0 0 var(--spacing-40);

      .header-legend {
        .header {
          font-size: 21px;
          margin-bottom: var(--spacing-24);
        }

        .legend {
          padding: 11px;

          p {
            font-size: 12px;
          }

          .line {
            width: 30px;
            height: 7px;
          }
        }
      }
    }

    button {
      margin: 0 auto;
    }
  }

  .two-tables {
    padding: 0 10px;

    h4 {
      text-align: center;
      font-size: 21px;
      margin-top: var(--spacing-48);
    }

    .gradient-wrapper {
      .first-table-wrapper,
      .second-table-wrapper {
        thead tr {
          th {
            font-size: 14px;
          }
        }

        tbody tr {
          th {
            font-size: 14px;
          }

          .headings {
            font-size: 14px;
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .calculator-container {
    .decimal-places {
      .dropdown::v-deep {
        &.open {
          .list {
            top: 101.9%;
          }
        }
      }

      .label-tooltip .icon-info-tooltip {
        &.linear-regression {
          &:hover {
            .tooltip:after {
              left: 46%;
            }
          }
        }
      }
    }

    .label-tooltip.your-data-tooltip .icon-info-tooltip {
      &.linear-regression {
        &:hover {
          .tooltip {
            left: 40px;

            &:after {
              left: 35.21%;
            }
          }
        }
      }
    }
  }

  .summary-container {
    div {
      overflow-y: hidden;

      .result-value {
        white-space: nowrap;
      }
    }
  }

  div .interpretation-container {
    padding: var(--spacing-32) var(--spacing-20);
  }
}

@import '../../../../assets/sass/ds/calculators/calculators.scss';
</style>
